import { useSelector } from 'react-redux';

import {
  getIsBookingForSelfOrDefaultToTrue,
  getIsInstantTaxCreditSelected,
} from '@front/common/src/ducks/customer/basket-selectors';
import type { User } from '@front/common/src/types/commons';

import { useHasCesuWallet } from './use-has-cesu-wallet';

export type UseIsITCSelectedOrSuccessfullyPairedParams = {
  user?: User;
  // CESU (FR-only, cleaning) replaces the tax credit when the signed-in customer has a positive wallet.
  // Off by default so mobile, which is out of CESU scope for the MVP, is unaffected; web screens opt in.
  // Must stay off on mobile: useHasCesuWallet checks auth via useIsSignedIn, which only works in the web SPAs.
  isCesuEnabled?: boolean;
};

export const useIsITCSelectedOrSuccessfullyPaired = ({
  user,
  isCesuEnabled = false,
}: UseIsITCSelectedOrSuccessfullyPairedParams) => {
  // if isInstantTaxCreditSelected is undefined, we set it to false
  const isInstantTaxCreditSelected = useSelector(getIsInstantTaxCreditSelected) ?? false;
  const isBookingForSelf = useSelector(getIsBookingForSelfOrDefaultToTrue);

  // CESU only applies when booking for oneself, so skip the wallet fetch otherwise. We gate here rather
  // than in useHasCesuWallet so customer-selection keeps fetching the wallet for someone else.
  const { hasCesuWallet } = useHasCesuWallet({ enabled: isCesuEnabled && isBookingForSelf });

  const isWecasaCarePairingSuccessful = user?.wecasa_care_pairing_status === 'successful';

  if (isCesuEnabled && hasCesuWallet) {
    return {
      isInstantTaxCreditSelected: false,
      isWecasaCarePairingSuccessful: false,
      isITCSelectedOrSuccessfullyPaired: false,
      shouldShowTaxCreditPriceInfo: false,
    };
  }

  const isITCSelectedOrSuccessfullyPaired =
    isBookingForSelf && (isWecasaCarePairingSuccessful || isInstantTaxCreditSelected);

  return {
    isInstantTaxCreditSelected,
    isWecasaCarePairingSuccessful,
    isITCSelectedOrSuccessfullyPaired,
    shouldShowTaxCreditPriceInfo: isBookingForSelf,
  };
};
